import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, subtitle }) => (
  <div className="flex items-center gap-3 p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
      {icon}
    </div>
    <div>
      <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
      {subtitle && (
        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
      )}
    </div>
  </div>
);

const ManageBookingPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [booking, setBooking] = useState(null);
  const [relatedBookings, setRelatedBookings] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Fetch booking details
  useEffect(() => {
    const fetchBooking = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/public/bookings/manage/${token}`);
        
        if (response.data.ok) {
          setBooking(response.data.booking);
          setRelatedBookings(response.data.related_bookings || []);
        } else {
          setError(response.data.message || 'Failed to load booking');
        }
      } catch (err) {
        console.error('Error fetching booking:', err);
        setError(
          err.response?.data?.message || 
          'This booking link is invalid or has expired.'
        );
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchBooking();
    }
  }, [token]);

  const handleCancelBooking = async (reason) => {
    try {
      setActionLoading(true);
      const response = await axios.post(`/api/public/bookings/manage/${token}/cancel`, {
        cancel_reason: reason
      });

      if (response.data.ok) {
        toast.success('Booking cancelled successfully');
        setBooking({ ...booking, status: 'cancelled' });
        setRelatedBookings((prev) =>
          prev.map((item) =>
            item.is_current_booking ? { ...item, status: 'cancelled' } : item
          )
        );
        setShowCancelModal(false);
      } else {
        toast.error(response.data.message || 'Failed to cancel booking');
      }
    } catch (err) {
      console.error('Error cancelling booking:', err);
      toast.error(
        err.response?.data?.message || 
        'Failed to cancel booking. Please try again.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-50 via-white to-zinc-100 dark:from-zinc-900 dark:via-black dark:to-zinc-800">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[--accent] mx-auto mb-4" style={{ '--accent': ACCENT }}></div>
          <p className="text-zinc-600 dark:text-zinc-400">Loading your booking...</p>
        </motion.div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-50 via-white to-zinc-100 dark:from-zinc-900 dark:via-black dark:to-zinc-800 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <Card className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-3">
              Booking Not Found
            </h2>
            <p className="text-zinc-600 dark:text-zinc-400 mb-6">
              {error}
            </p>
            <button
              onClick={() => navigate('/booking/resend-link')}
              className="w-full py-3 px-6 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg"
              style={{ backgroundColor: ACCENT }}
            >
              Request New Link
            </button>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Cancelled Booking State
  if (booking && booking.status === 'cancelled') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-50 via-white to-zinc-100 dark:from-zinc-900 dark:via-black dark:to-zinc-800 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <Card className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-zinc-600 dark:text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-3">
              Booking Cancelled
            </h2>
            <p className="text-zinc-600 dark:text-zinc-400 mb-6">
              This booking has been cancelled.
            </p>
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 px-6 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg"
              style={{ backgroundColor: ACCENT }}
            >
              Book Again
            </button>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Main Booking View
  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 via-white to-zinc-100 dark:from-zinc-900 dark:via-black dark:to-zinc-800 p-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
            Your Booking
          </h1>
          <p className="text-zinc-600 dark:text-zinc-400">
            Manage your appointment details
          </p>
        </div>

        {/* Booking Details Card */}
        <Card className="mb-6">
          <CardHeader
            title="Booking Details"
            icon={
              <svg className="w-5 h-5" style={{ color: ACCENT }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
          />
          
          <div className="p-6 space-y-4">
            {/* Status Badge */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-600 dark:text-zinc-400">Status:</span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                booking.status === 'confirmed' 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
              }`}>
                {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
              </span>
            </div>

            {/* Details Grid */}
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <span className="text-sm text-zinc-600 dark:text-zinc-400">Salon</span>
                <span className="font-medium text-zinc-900 dark:text-zinc-100">{booking.shop_name}</span>
              </div>
              
              <div className="flex justify-between items-start">
                <span className="text-sm text-zinc-600 dark:text-zinc-400">Service</span>
                <span className="font-medium text-zinc-900 dark:text-zinc-100">{booking.service_name}</span>
              </div>
              
              <div className="flex justify-between items-start">
                <span className="text-sm text-zinc-600 dark:text-zinc-400">Barber</span>
                <span className="font-medium text-zinc-900 dark:text-zinc-100">{booking.barber_name}</span>
              </div>
              
              <div className="flex justify-between items-start">
                <span className="text-sm text-zinc-600 dark:text-zinc-400">Date</span>
                <span className="font-medium text-zinc-900 dark:text-zinc-100">{formatDate(booking.start_time)}</span>
              </div>
              
              <div className="flex justify-between items-start">
                <span className="text-sm text-zinc-600 dark:text-zinc-400">Time</span>
                <span className="font-medium text-zinc-900 dark:text-zinc-100">{formatTime(booking.start_time)}</span>
              </div>

              {booking.notes && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">Notes</span>
                  <span className="font-medium text-zinc-900 dark:text-zinc-100 text-right max-w-xs">{booking.notes}</span>
                </div>
              )}
            </div>
          </div>
        </Card>

        {relatedBookings.length > 0 && (
          <Card className="mb-6">
            <CardHeader
              title="Your Bookings"
              subtitle="Sorted from oldest to newest"
              icon={
                <svg className="w-5 h-5" style={{ color: ACCENT }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              }
            />

            <div className="p-6 space-y-3">
              {relatedBookings.map((item, index) => (
                <div
                  key={`${item.start_time}-${index}`}
                  className={`rounded-xl border px-4 py-3 ${
                    item.is_current_booking
                      ? 'border-[--accent] bg-[rgba(125,102,51,0.08)]'
                      : 'border-white/20 dark:border-white/10 bg-white/40 dark:bg-zinc-800/40'
                  }`}
                  style={{ '--accent': ACCENT }}
                >
                  <div className="flex items-center justify-between gap-4 mb-1">
                    <div className="font-medium text-zinc-900 dark:text-zinc-100">
                      {item.service_name}
                    </div>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.status === 'confirmed'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : item.status === 'cancelled'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                    }`}>
                      {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                    </span>
                  </div>
                  <div className="text-sm text-zinc-600 dark:text-zinc-400">
                    {formatDate(item.start_time)} at {formatTime(item.start_time)}
                  </div>
                  <div className="text-sm text-zinc-600 dark:text-zinc-400">
                    {item.barber_name} at {item.shop_name}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Action Buttons */}
        {booking.status !== 'cancelled' && (
          <Card className="p-6">
            <div className="space-y-3">
              <button
                onClick={() => setShowRescheduleModal(true)}
                disabled={actionLoading}
                className="w-full py-3 px-6 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: ACCENT }}
              >
                Reschedule Booking
              </button>
              
              <button
                onClick={() => setShowCancelModal(true)}
                disabled={actionLoading}
                className="w-full py-3 px-6 rounded-xl font-medium border-2 transition-all duration-200 hover:bg-red-50 dark:hover:bg-red-900/10 text-red-600 dark:text-red-400 border-red-300 dark:border-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel Booking
              </button>
            </div>
          </Card>
        )}

        {/* Resend Link */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/booking/resend-link')}
            className="text-sm text-zinc-600 dark:text-zinc-400 hover:text-[--accent] dark:hover:text-[--accent] transition-colors"
            style={{ '--accent': ACCENT }}
          >
            Lost your booking link? Request a new one
          </button>
        </div>
      </motion.div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md w-full"
          >
            <Card className="p-6">
              <h3 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
                Cancel Booking?
              </h3>
              <p className="text-zinc-600 dark:text-zinc-400 mb-6">
                Are you sure you want to cancel your booking? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  disabled={actionLoading}
                  className="flex-1 py-2.5 px-4 rounded-xl font-medium border-2 border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all disabled:opacity-50"
                >
                  Keep Booking
                </button>
                <button
                  onClick={() => handleCancelBooking('Cancelled by client')}
                  disabled={actionLoading}
                  className="flex-1 py-2.5 px-4 rounded-xl font-medium bg-red-600 text-white hover:bg-red-700 transition-all disabled:opacity-50"
                >
                  {actionLoading ? 'Cancelling...' : 'Yes, Cancel'}
                </button>
              </div>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Reschedule Modal - Placeholder */}
      {showRescheduleModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md w-full"
          >
            <Card className="p-6">
              <h3 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
                Reschedule Booking
              </h3>
              <p className="text-zinc-600 dark:text-zinc-400 mb-6">
                To reschedule your booking, please contact the salon directly at {booking.shop_name}.
              </p>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6">
                Online rescheduling is coming soon!
              </p>
              <button
                onClick={() => setShowRescheduleModal(false)}
                className="w-full py-2.5 px-4 rounded-xl font-medium text-white transition-all"
                style={{ backgroundColor: ACCENT }}
              >
                Got it
              </button>
            </Card>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ManageBookingPage;
